import React, {useEffect, useMemo, useState} from 'react';

import {orderAdminApi} from './orderAdminApi';
import styles from './OrderAdminDashboard.module.css';

const PRIMARY_KEY = 'task_id';
const CREATED_AT = 'created_at';
const DATA_FORMAT = 'data_format';
const PREVIEW_VIDEO_URL = 'preview_video_url';
const PREVIEW_VIDEO_UPLOAD_ID = 'preview_video_upload_id';
const IS_VISIBLE_BETA = 'is_visible_beta';
const IS_VISIBLE_PRODUCTION = 'is_visible_production';
const VISIBILITY_FIELDS = new Set([IS_VISIBLE_BETA, IS_VISIBLE_PRODUCTION]);
const DEFAULT_DATA_FORMAT = 'mcap/mp4';

const labelFor = (value) => String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const shouldUseTextarea = (column) => /(description|details|notes|objects|variations)/i.test(column);

function normalizeColumnDetails(details) {
    return Object.fromEntries(Object.entries(details || {}).map(([column, detail]) => [column, {
        input_type: detail?.input_type || 'text',
        data_type: detail?.data_type || 'text',
        required: Boolean(detail?.required),
    }]));
}

function validateValue(column, value, detail) {
    const normalized = String(value ?? '').trim();
    if (!normalized) return detail?.required ? `${labelFor(column)} is required.` : '';
    if (detail?.input_type === 'integer' && !/^[+-]?\d+$/.test(normalized)) return `${labelFor(column)} must be an integer.`;
    if (detail?.input_type === 'number' && Number.isNaN(Number(normalized))) return `${labelFor(column)} must be a number.`;
    if (detail?.input_type === 'boolean' && !/^(true|false|1|0|yes|no|y|n)$/i.test(normalized)) return `${labelFor(column)} must be true or false.`;
    return '';
}

export default function CatalogTaskCreateModal({adminToken, initialScenario, onClose, onCreated}) {
    const [columns, setColumns] = useState([]);
    const [columnDetails, setColumnDetails] = useState({});
    const [formRow, setFormRow] = useState({});
    const [previewVideoUploadId, setPreviewVideoUploadId] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [createdTask, setCreatedTask] = useState(null);

    const formColumns = useMemo(() => columns.filter((column) => (
        column !== PRIMARY_KEY
        && column !== CREATED_AT
        && !VISIBILITY_FIELDS.has(column)
    )), [columns]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        orderAdminApi.getCatalogTaskFormSchema(adminToken)
            .then((meta) => {
                if (cancelled) return;
                const nextColumns = Array.from(new Set(meta.columns || []));
                const nextDetails = normalizeColumnDetails(meta.column_details);
                const nextRow = {};
                nextColumns.forEach((column) => { nextRow[column] = ''; });
                nextRow.scenario = initialScenario || '';
                nextRow[DATA_FORMAT] = DEFAULT_DATA_FORMAT;
                nextRow[IS_VISIBLE_BETA] = 'true';
                nextRow[IS_VISIBLE_PRODUCTION] = 'true';
                setColumns(nextColumns);
                setColumnDetails(nextDetails);
                setFormRow(nextRow);
            })
            .catch((schemaError) => {
                if (!cancelled) setError(schemaError.message || 'Failed to load the Task form.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [adminToken, initialScenario]);

    const linkCreatedTask = async (task) => {
        try {
            setSaving(true);
            setError('');
            await onCreated(task);
        } catch (linkError) {
            setCreatedTask(task);
            setError(`Task #${task.task_id} was created, but it could not be linked. ${linkError.message || 'Try linking it again.'}`);
        } finally {
            setSaving(false);
        }
    };

    const submit = async (event) => {
        event.preventDefault();
        if (createdTask) {
            await linkCreatedTask(createdTask);
            return;
        }
        const payload = {};
        for (const column of formColumns) {
            if (column === PREVIEW_VIDEO_URL && String(previewVideoUploadId).trim()) continue;
            const validationError = validateValue(column, formRow[column], columnDetails[column]);
            if (validationError) {
                setError(validationError);
                return;
            }
            const value = formRow[column] ?? '';
            if (String(value).trim() || columnDetails[column]?.required) payload[column] = value;
        }
        payload[IS_VISIBLE_BETA] = formRow[IS_VISIBLE_BETA] || 'true';
        payload[IS_VISIBLE_PRODUCTION] = formRow[IS_VISIBLE_PRODUCTION] || 'true';
        if (String(previewVideoUploadId).trim()) payload[PREVIEW_VIDEO_UPLOAD_ID] = previewVideoUploadId.trim();

        let task;
        try {
            setSaving(true);
            setError('');
            task = await orderAdminApi.createCatalogTask(adminToken, payload);
            setCreatedTask(task);
        } catch (submitError) {
            setError(submitError.message || 'Failed to create the Task.');
            setSaving(false);
            return;
        }
        await linkCreatedTask(task);
    };

    return <div className={styles.modalBackdrop} role="presentation">
        <form className={styles.catalogTaskModal} role="dialog" aria-modal="true" aria-labelledby="create-catalog-task-title" onSubmit={submit}>
            <header>
                <div><h2 id="create-catalog-task-title">Create Task</h2><p>Create the canonical catalog Task, then link it to “{initialScenario}”.</p></div>
                <button type="button" aria-label="Close create Task modal" disabled={saving} onClick={onClose}>×</button>
            </header>
            <div className={styles.catalogTaskModalBody}>
                {loading ? <div className={styles.taskModalState}>Loading Task fields…</div> : null}
                {!loading ? formColumns.map((column) => {
                    const detail = columnDetails[column];
                    if (column === PREVIEW_VIDEO_URL) return <label key={column}>{labelFor(PREVIEW_VIDEO_UPLOAD_ID)}
                        <input value={previewVideoUploadId} inputMode="numeric" onChange={(event) => setPreviewVideoUploadId(event.target.value)} placeholder="Optional upload ID"/>
                        <small>Optional · resolves the preview video URL from an Upload.</small>
                    </label>;
                    const commonProps = {
                        value: formRow[column] ?? '',
                        onChange: (event) => setFormRow((current) => ({...current, [column]: event.target.value})),
                        placeholder: `${detail?.required ? 'Required' : 'Optional'} · ${detail?.input_type || detail?.data_type || 'text'}`,
                    };
                    return <label key={column}>{labelFor(column)}{detail?.required ? <em>*</em> : null}
                        {shouldUseTextarea(column) ? <textarea {...commonProps} rows={3}/> : <input {...commonProps} inputMode={detail?.input_type === 'integer' || detail?.input_type === 'number' ? 'numeric' : undefined}/>}
                    </label>;
                }) : null}
                {!loading ? <div className={styles.catalogVisibilityGrid}>
                    <label>Visible in Beta<input value={formRow[IS_VISIBLE_BETA] ?? 'true'} onChange={(event) => setFormRow((current) => ({...current, [IS_VISIBLE_BETA]: event.target.value}))}/></label>
                    <label>Visible in Production<input value={formRow[IS_VISIBLE_PRODUCTION] ?? 'true'} onChange={(event) => setFormRow((current) => ({...current, [IS_VISIBLE_PRODUCTION]: event.target.value}))}/></label>
                </div> : null}
                {error ? <div className={styles.editorError}>{error}</div> : null}
            </div>
            <footer>
                <button type="button" disabled={saving} onClick={onClose}>Cancel</button>
                <button type="submit" className={styles.primaryButton} disabled={saving || loading || (!createdTask && !columns.length)}>{saving ? (createdTask ? 'Linking…' : 'Creating…') : createdTask ? 'Retry link' : 'Create and link Task'}</button>
            </footer>
        </form>
    </div>;
}
